import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Collapse, Form, Input, InputNumber, Modal, Popconfirm, Progress, Select, Space, Switch, Table, Tag, Tooltip, Typography, message } from 'antd';
import { CheckCircleTwoTone, CloseCircleTwoTone, DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import evmAdminService from '../../../shared/services/evmAdminService';

const { Text } = Typography;

const gb = (v) => (v === null || v === undefined ? '-' : `${Math.round(v)} GB`);
const HEALTH = { HEALTH_OK: 'success', HEALTH_WARN: 'warning', HEALTH_ERR: 'error' };
const errMsg = (res, fallback) => res?.msg || res?.error || fallback;

// 已挂载节点 + 挂载到新节点
const ClusterNodes = ({ cluster, nodes, act }) => {
    const [pick, setPick] = useState(undefined);
    const nodeName = useMemo(() => Object.fromEntries(nodes.map((n) => [n.id, n.name])), [nodes]);
    const attached = new Set((cluster.nodes || []).map((n) => n.node_id));
    const columns = [
        { title: '节点', dataIndex: 'node_id', render: (id) => nodeName[id] || `#${id}` },
        {
            title: '已生效', dataIndex: 'applied', width: 90,
            render: (v, r) => (v
                ? <CheckCircleTwoTone twoToneColor="#52c41a" />
                : <Tooltip title={r.error || '等待节点应用'}><CloseCircleTwoTone twoToneColor={r.error ? '#ff4d4f' : '#faad14'} /></Tooltip>),
        },
        { title: '云盘', dataIndex: 'disks', width: 70 },
        {
            title: '', width: 60, render: (_, r) => (
                <Popconfirm title="从该节点卸载？" disabled={r.disks > 0} onConfirm={() => act(evmAdminService.detachCephCluster(cluster.id, r.node_id), '已卸载')}>
                    <Tooltip title={r.disks > 0 ? '仍有云盘' : '卸载'}>
                        <Button size="small" danger icon={<DeleteOutlined />} disabled={r.disks > 0} />
                    </Tooltip>
                </Popconfirm>
            ),
        },
    ];
    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Table rowKey="node_id" size="small" columns={columns} dataSource={cluster.nodes || []} pagination={false} />
            <Space>
                <Select placeholder="选择节点" style={{ width: 220 }} value={pick} onChange={setPick} showSearch optionFilterProp="label"
                    options={nodes.filter((n) => !attached.has(n.id)).map((n) => ({ value: n.id, label: n.name }))} />
                <Button type="primary" disabled={!pick}
                    onClick={async () => { if (await act(evmAdminService.attachCephCluster(cluster.id, pick), '已挂载')) setPick(undefined); }}>
                    挂载到节点
                </Button>
            </Space>
        </Space>
    );
};

const CephClusterManagement = () => {
    const [data, setData] = useState({ clusters: [], nodes: [] });
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null); // {} = new
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        const res = await evmAdminService.getCephClusters();
        setLoading(false);
        if (res?.status === 'success') setData({ clusters: res.data?.clusters || [], nodes: res.data?.nodes || [] });
        else message.error(errMsg(res, '加载失败'));
    }, []);

    useEffect(() => { load(); }, [load]);

    const act = async (p, ok = '已保存') => {
        const res = await p;
        if (res?.status === 'success') { message.success(res.msg || ok); load(); return true; }
        message.error(errMsg(res, '操作失败'));
        return false;
    };

    const openEdit = (row) => {
        setEditing(row || {});
        form.resetFields();
        form.setFieldsValue(row
            ? { ...row, monitors: (row.monitors || []).join(', '), key: '', size_gb: row.size_gb || null }
            : { user: 'admin', oversell_ratio: 1 });
    };

    const submit = async () => {
        const v = await form.validateFields();
        const body = { ...v, size_gb: v.size_gb || 0 };
        if (editing.id && !body.key) delete body.key;
        const ok = editing.id
            ? await act(evmAdminService.updateCephCluster(editing.id, body))
            : await act(evmAdminService.createCephCluster(body), '已创建');
        if (ok) setEditing(null);
    };

    const columns = [
        { title: '名称', dataIndex: 'name', width: 150 },
        { title: 'Pool', render: (_, r) => <Text code>{r.pool}{r.namespace ? `/${r.namespace}` : ''}</Text> },
        {
            title: '健康', dataIndex: 'health', width: 120,
            render: (h, r) => {
                if (r.report_error) return <Tooltip title={r.report_error}><Tag color="error">上报失败</Tag></Tooltip>;
                if (!h) return <Text type="warning">未上报</Text>;
                return <Tooltip title={r.latency_ms !== null && r.latency_ms !== undefined ? `${r.latency_ms} ms` : ''}><Tag color={HEALTH[h] || 'default'}>{h.replace('HEALTH_', '')}</Tag></Tooltip>;
            },
        },
        {
            title: '物理 已用 / 总量', width: 190,
            render: (_, r) => (r.total_gb ? (
                <div>
                    <Progress size="small" percent={Math.round((r.used_gb || 0) / r.total_gb * 100)} />
                    <Text type={r.fresh ? 'secondary' : 'warning'}>{gb(r.used_gb)} / {gb(r.total_gb)}{r.fresh ? '' : '（过期）'}</Text>
                </div>
            ) : <Text type="warning">未上报</Text>),
        },
        {
            title: <Tooltip title="已售 = 云盘容量合计；上限 = 总量 × 超售比（或手动上限）">已售 / 上限</Tooltip>, width: 190,
            render: (_, r) => (
                <div>
                    <Progress size="small" showInfo={false} percent={r.limit_gb ? Math.min(100, Math.round(r.committed_gb / r.limit_gb * 100)) : 0} />
                    <Text type="secondary">{gb(r.committed_gb)} / {r.limit_gb === null ? '-' : gb(r.limit_gb)}</Text>
                </div>
            ),
        },
        { title: '节点', width: 80, render: (_, r) => { const n = r.nodes || []; return `${n.filter((x) => x.applied).length} / ${n.length}`; } },
        {
            title: '启用', dataIndex: 'status', width: 70,
            render: (s, r) => <Switch size="small" checked={s === 'enable'} onChange={(on) => act(evmAdminService.updateCephCluster(r.id, { status: on ? 'enable' : 'disable' }))} />,
        },
        {
            title: '', width: 90, render: (_, r) => {
                const busy = (r.nodes || []).length > 0;
                return (
                    <Space size={4}>
                        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                        <Popconfirm title="删除该集群？" disabled={busy} onConfirm={() => act(evmAdminService.deleteCephCluster(r.id), '已删除')}>
                            <Tooltip title={busy ? '先从所有节点卸载' : ''}>
                                <Button size="small" danger icon={<DeleteOutlined />} disabled={busy} />
                            </Tooltip>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    const hasDisks = editing?.id && (editing.nodes || []).some((n) => n.disks > 0);

    return (
        <Card
            title="Ceph 存储集群"
            extra={(
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={load} />
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openEdit(null)}>新建</Button>
                </Space>
            )}
        >
            <Table rowKey="id" size="small" loading={loading} columns={columns} dataSource={data.clusters} pagination={false} scroll={{ x: 1000 }}
                expandable={{ expandedRowRender: (r) => <ClusterNodes cluster={r} nodes={data.nodes} act={act} /> }} />

            <Modal title={editing?.id ? '编辑集群' : '新建集群'} open={!!editing} onOk={submit} onCancel={() => setEditing(null)} destroyOnClose>
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="名称" rules={[{ required: true }]}><Input /></Form.Item>
                    <Form.Item name="monitors" label="Monitor" rules={[{ required: true }]} tooltip="多个用逗号或空格分隔">
                        <Input placeholder="10.0.0.1:6789, 10.0.0.2:6789" />
                    </Form.Item>
                    <Space size="large">
                        <Form.Item name="pool" label="Pool" rules={[{ required: true }]}><Input style={{ width: 160 }} disabled={hasDisks} /></Form.Item>
                        <Form.Item name="user" label="用户" rules={[{ required: true }]}><Input style={{ width: 160 }} placeholder="admin" disabled={hasDisks} /></Form.Item>
                    </Space>
                    <Form.Item name="key" label="密钥" rules={editing?.id ? [] : [{ required: true }]} tooltip="cephx key 或 keyring 内容">
                        <Input.Password placeholder={editing?.id ? '留空不修改' : ''} autoComplete="new-password" />
                    </Form.Item>
                    <Collapse ghost size="small" items={[{
                        key: 'adv',
                        label: '高级',
                        forceRender: true,
                        children: (
                            <>
                                <Space size="large" wrap>
                                    <Form.Item name="namespace" label="Namespace"><Input style={{ width: 160 }} disabled={hasDisks} /></Form.Item>
                                    <Form.Item name="oversell_ratio" label="超售比"><InputNumber min={1} max={10} step={0.5} style={{ width: 100 }} /></Form.Item>
                                    <Form.Item name="size_gb" label="手动上限 (GB)" tooltip="留空 = 自动（总量 × 超售比）">
                                        <InputNumber min={1} placeholder="自动" style={{ width: 130 }} />
                                    </Form.Item>
                                </Space>
                                <Form.Item name="fsid" label="FSID"><Input /></Form.Item>
                            </>
                        ),
                    }]} />
                </Form>
            </Modal>
        </Card>
    );
};

export default CephClusterManagement;
